import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { WeightInput } from '../products/ProductFilters/ProductFilters.styles';

export interface RangeNumberInputHandle {
  /** Confirma el borrador. `null` = inválido (no se aplica). */
  commit: () => number | undefined | null;
}

export interface RangeNumberInputProps {
  value: number | undefined;
  defaultDisplayValue: number;
  onCommit: (value: number | undefined) => void;
  onEnter: (value: number | undefined) => void;
  min?: number;
  max: number;
  /** When true, allows decimal input (e.g. price per gram). */
  allowDecimals?: boolean;
  decimalPlaces?: number;
}

function parseDraftText(
  text: string,
  min: number,
  max: number,
  allowDecimals: boolean,
  decimalPlaces: number,
): number | undefined | null {
  const trimmed = text.trim();
  if (trimmed === '') return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < min || n > max) return null;
  if (!allowDecimals) return n;
  const factor = 10 ** decimalPlaces;
  return Math.round(n * factor) / factor;
}

export const RangeNumberInput = forwardRef<RangeNumberInputHandle, RangeNumberInputProps>(
  function RangeNumberInput(
    {
      value,
      defaultDisplayValue,
      onCommit,
      onEnter,
      min = 0,
      max,
      allowDecimals = false,
      decimalPlaces = 2,
    },
    ref,
  ) {
    const [draft, setDraft] = useState('');
    const [focused, setFocused] = useState(false);
    const draftRef = useRef(draft);
    draftRef.current = draft;

    useEffect(() => {
      if (!focused) {
        setDraft(value != null ? String(value) : '');
      }
    }, [value, focused]);

    const commitDraft = useCallback((): number | undefined | null => {
      const parsed = parseDraftText(draftRef.current, min, max, allowDecimals, decimalPlaces);
      if (parsed === null) {
        setDraft(value != null ? String(value) : '');
        return null;
      }
      onCommit(parsed);
      setDraft(parsed != null ? String(parsed) : '');
      return parsed;
    }, [min, max, allowDecimals, decimalPlaces, onCommit, value]);

    useImperativeHandle(ref, () => ({ commit: commitDraft }), [commitDraft]);

    const handleFocus = useCallback(() => {
      setFocused(true);
      setDraft(value != null ? String(value) : String(defaultDisplayValue));
    }, [value, defaultDisplayValue]);

    const handleBlur = useCallback(() => {
      setFocused(false);
      commitDraft();
    }, [commitDraft]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        if (allowDecimals) {
          if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
            setDraft(raw);
          }
          return;
        }
        if (raw === '' || /^\d+$/.test(raw)) {
          setDraft(raw);
        }
      },
      [allowDecimals],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const parsed = parseDraftText(draftRef.current, min, max, allowDecimals, decimalPlaces);
        if (parsed === null) return;
        onCommit(parsed);
        setDraft(parsed != null ? String(parsed) : '');
        onEnter(parsed);
      },
      [min, max, allowDecimals, decimalPlaces, onCommit, onEnter],
    );

    const displayValue = focused
      ? draft
      : value != null
        ? String(value)
        : String(defaultDisplayValue);

    return (
      <WeightInput
        type="text"
        inputMode={allowDecimals ? 'decimal' : 'numeric'}
        pattern={allowDecimals ? undefined : '[0-9]*'}
        value={displayValue}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
    );
  },
);

export default RangeNumberInput;
